import type { Raster } from '@/modules/Editor/models/raster/Raster'
import { RasterTypes } from '@/modules/Editor/models/raster/rasterTypes'
import { assertImageRaster } from '@/modules/Editor/models/raster/assertImageRaster'
import { assertVideoRaster } from '@/modules/Editor/models/raster/assertVideoRaster'
import type { BBox } from '@/modules/Editor/types'
// eslint-disable-next-line boundaries/element-types
import { calculateCentroidFromRasterBounds } from '@/utils/centroid/calculateCentroidFromRasterBounds'
import { getXYVoxelScaling } from '@/modules/Editor/plugins/maskBrush/utils/getXYVoxelScaling'
import { getVoxelRasterDimensions } from '@/modules/Editor/plugins/maskBrush/utils/getVoxelRasterDimensions'
import type { IPoint } from '@/core/annotations'

export const getMaskAnnotationBBox = (
  raster: Raster,
  annotationId: string,
  frameIndex: number,
): BBox | undefined => {
  const labelIndex = raster.getLabelIndexForAnnotationId(annotationId)
  if (!labelIndex) {
    return undefined
  }

  if (raster.type === RasterTypes.IMAGE) {
    const imageRaster = assertImageRaster(raster)
    const bounds = imageRaster.getBoundsForLabelIndex(labelIndex)
    if (!bounds) {
      return undefined
    }

    return {
      x: bounds.topLeft.x + (bounds.bottomRight.x - bounds.topLeft.x) / 2,
      y: bounds.topLeft.y + (bounds.bottomRight.y - bounds.topLeft.y) / 2,
      width: bounds.bottomRight.x - bounds.topLeft.x,
      height: bounds.bottomRight.y - bounds.topLeft.y,
    }
  }

  if (raster.type === RasterTypes.VIDEO || raster.type === RasterTypes.VOXEL) {
    const videoRaster = assertVideoRaster(raster)

    const centroid = calculateCentroidFromRasterBounds(videoRaster, labelIndex, frameIndex)
    if (!centroid) {
      return
    }

    const frameBounds = videoRaster.getVideoBoundsForLabelIndexForFrame(labelIndex, frameIndex)
    if (!frameBounds) {
      return
    }

    const { metadata } = raster.view.fileManager.file
    const rasterDimension = getVoxelRasterDimensions(raster.view)
    let voxelScaling: IPoint = { x: 1, y: 1 }

    if (metadata && rasterDimension) {
      voxelScaling = getXYVoxelScaling(metadata, rasterDimension) || {
        x: 1,
        y: 1,
      }
    }

    return {
      x: centroid.x,
      y: centroid.y,
      width: (frameBounds.bottomRight.x - frameBounds.topLeft.x) * voxelScaling.x,
      height: (frameBounds.bottomRight.y - frameBounds.topLeft.y) * voxelScaling.y,
    }
  }

  return undefined
}
